import { set } from 'lodash'
import React, { useState, useEffect } from 'react'

import './ClickableTextArea.scss'
import { useTranslation } from 'react-i18next'

const urlRegex = /\b((https?:\/\/)?(www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/[\w\-./?%&=+#]*)?(?<!\.pdf))\b/gi
export default function LinkifiedTextarea({
  text: initialText = '',
  onChange,
  references
} = {}) {
  const [text, setText] = useState(initialText) // Initialize with initialText directly
  const [popup, setPopup] = useState({
    visible: false,
    x: 0,
    y: 0,
    href: '',
    isPdfReference: false
  })

  const { t } = useTranslation()

  // Update text when initialText prop changes
  useEffect(() => {
    setText(initialText)
  }, [initialText])

  // Scroll sync
  useEffect(() => {
    const textarea = document.getElementById('text-layer')
    const highlight = document.getElementById('highlight-layer')

    if (!textarea || !highlight) return

    const syncScroll = () => {
      highlight.scrollTop = textarea.scrollTop
      highlight.scrollLeft = textarea.scrollLeft
    }

    textarea.addEventListener('scroll', syncScroll)
    return () => textarea.removeEventListener('scroll', syncScroll)
  }, [])

  const getHighlightedText = input => {
    if (!input) return ''
    const escaped = input
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')

    let highlighted = escaped.replace(urlRegex, url => {
      let href = url
      if (!href.match(/^https?:\/\//)) {
        href = 'http://' + href
      }
      return `<span class="link" data-href="${href}" style="color:#5057d5;text-decoration:underline;cursor:pointer;">${url}</span>`
    })

    // Handle PDF references like [1], [2], etc.
    if (references && typeof references === 'object') {
      highlighted = highlighted.replace(/\[(\d+)\]/g, (match, refNumber) => {
        const pdfUrl = references[refNumber]
        if (pdfUrl) {
          return `<span class="link pdf-reference" data-href="${pdfUrl}" style="color:#5057d5;text-decoration:underline;cursor:pointer;font-weight:bold;">${match}</span>`
        }
        return match // Return original if no reference found
      })
    }

    return highlighted.replace(/\n/g, '<br/>')
  }

  const handleClick = e => {
    const container = document.getElementById('container-layer')
    const textarea = document.getElementById('text-layer')
    const highlight = document.getElementById('highlight-layer')
    if (!container || !textarea || !highlight) return

    // Temporarily disable pointer events on textarea and container to let elementFromPoint detect highlight links
    container.style.pointerEvents = 'none'
    textarea.style.pointerEvents = 'none'

    // Get element at click position
    const element = document.elementFromPoint(e.clientX, e.clientY)

    // Restore pointer events immediately
    container.style.pointerEvents = 'auto'
    textarea.style.pointerEvents = 'auto'

    if (!element) return

    const linkSpan = element.closest('.link')
    if (linkSpan && highlight.contains(linkSpan)) {
      // You clicked a link span!
      console.log('Clicked link span:', linkSpan)
      const href = linkSpan.getAttribute('data-href')
      const isPdfReference = linkSpan.classList.contains('pdf-reference')
      setPopup({
        visible: true,
        x: e.clientX,
        y: e.clientY,
        href: href || '',
        isPdfReference: isPdfReference
      })
    } else {
      // Clicked outside of link spans, hide popup if visible
      if (popup.visible) {
        setPopup({
          visible: false,
          x: 0,
          y: 0,
          href: '',
          isPdfReference: false
        })
      }
    }
  }

  const handleTextChange = e => {
    const newText = e.target.value
    setText(newText)
    if (onChange) {
      onChange(newText) // Call onChange prop if provided
    }
  }

  const handleClickPopup = () => {
    if (popup.href) {
      window.open(popup.href, '_blank', 'noopener,noreferrer')
      setPopup({ visible: false, x: 0, y: 0, href: '', isPdfReference: false })
    }
  }

  return (
    <div
      id="container-layer"
      onClick={handleClick}
      style={{
        position: 'relative',
        width: '100%',
        height: '600px',
        backgroundColor: 'white',
        cursor: 'text',
        userSelect: 'none'
      }}
    >
      {/* Highlight layer */}
      <div
        id="highlight-layer"
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'white',
          color: '#000',
          whiteSpace: 'pre-wrap',
          overflowWrap: 'break-word',
          overflow: 'auto',
          padding: 8,
          fontSize: 14,
          zIndex: 1,
          pointerEvents: 'auto',
          wordBreak: 'break-word',
          lineHeight: '1.2',
          boxSizing: 'border-box'
        }}
        dangerouslySetInnerHTML={{ __html: getHighlightedText(text) }}
      />

      {/* Textarea */}
      <textarea
        id="text-layer"
        value={text}
        onChange={handleTextChange}
        spellCheck={false}
        style={{
          position: 'absolute',
          backgroundColor: 'transparent',
          color: 'transparent',
          caretColor: 'black',
          width: '100%',
          height: '100%',
          fontSize: 14,
          padding: 8,
          border: '1px solid #ccc',
          zIndex: 2,
          resize: 'none',
          overflow: 'auto',
          wordBreak: 'break-word',
          lineHeight: '1.2',
          boxSizing: 'border-box'
        }}
      />

      {/* Floating link popup */}
      {popup.visible && (
        <div
          onClick={handleClickPopup}
          style={{
            position: 'fixed',
            top: popup.y + 10,
            left: popup.x + 10,
            backgroundColor: '#5057d5',
            color: '#fff',
            padding: '8px 12px',
            borderRadius: 20,
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 10,
            textAlign: 'center',
            fontSize: 12,
            cursor: 'pointer',
            zIndex: 999,
            boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
            userSelect: 'none'
          }}
          className="popup-link"
        >
          {popup.isPdfReference
            ? `${t('v5:click_to_open_pdf_reference')}`
            : `${t('v5:click_to_open')} "${popup.href}"`}
        </div>
      )}
    </div>
  )
}
